import { onBeforeUnmount, onMounted } from 'vue'

type OnboardingClickLock = {
  active: boolean
  allowSelector: string
  blockButtonClicksInActiveElement: boolean
}

const PDL_ALLOWED_SELECTOR = '[data-onboarding="national-direction-pdl"] .orga-element-modify-content'

function isAllowedTarget(eventTarget: EventTarget | null, allowSelector: string): boolean {
  if (!import.meta.client) {
    return true
  }
  if (!(eventTarget instanceof Element)) {
    return false
  }
  return eventTarget.closest(allowSelector) !== null
}

function isButtonLikeTarget(eventTarget: EventTarget | null): boolean {
  if (!import.meta.client) {
    return false
  }
  if (!(eventTarget instanceof Element)) {
    return false
  }
  return eventTarget.closest('button, [role="button"], a, input[type="button"], input[type="submit"]') !== null
}

function isWithinDriverActiveElement(eventTarget: EventTarget | null): boolean {
  if (!import.meta.client) {
    return false
  }
  if (!(eventTarget instanceof Element)) {
    return false
  }
  return eventTarget.closest('.driver-active-element') !== null
}

export function useOnboardingClickLock() {
  const clickLock = useState<OnboardingClickLock>('onboarding.clickLock', () => ({
    active: false,
    allowSelector: PDL_ALLOWED_SELECTOR,
    blockButtonClicksInActiveElement: false,
  }))

  function onDocumentPointerDownCapture(event: Event): void {
    if (!clickLock.value.active) {
      return
    }
    if (clickLock.value.blockButtonClicksInActiveElement) {
      if (isWithinDriverActiveElement(event.target) && isButtonLikeTarget(event.target)) {
        event.preventDefault()
        event.stopPropagation()
      }
      return
    }
    if (isAllowedTarget(event.target, clickLock.value.allowSelector)) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
  }

  if (import.meta.client) {
    onMounted(() => {
      document.addEventListener('pointerdown', onDocumentPointerDownCapture, { capture: true })
      document.addEventListener('click', onDocumentPointerDownCapture, { capture: true })
      document.addEventListener('mousedown', onDocumentPointerDownCapture, { capture: true })
      document.addEventListener('touchstart', onDocumentPointerDownCapture, { capture: true })
    })
    onBeforeUnmount(() => {
      document.removeEventListener('pointerdown', onDocumentPointerDownCapture, { capture: true } as AddEventListenerOptions)
      document.removeEventListener('click', onDocumentPointerDownCapture, { capture: true } as AddEventListenerOptions)
      document.removeEventListener('mousedown', onDocumentPointerDownCapture, { capture: true } as AddEventListenerOptions)
      document.removeEventListener('touchstart', onDocumentPointerDownCapture, { capture: true } as AddEventListenerOptions)
    })
  }

  function activateClickLock(): void {
    clickLock.value = {
      active: true,
      allowSelector: PDL_ALLOWED_SELECTOR,
      blockButtonClicksInActiveElement: false,
    }
  }

  function activateBlockButtonsInActiveElement(): void {
    clickLock.value = {
      active: true,
      allowSelector: PDL_ALLOWED_SELECTOR,
      blockButtonClicksInActiveElement: true,
    }
  }

  function deactivateClickLock(): void {
    clickLock.value = {
      active: false,
      allowSelector: PDL_ALLOWED_SELECTOR,
      blockButtonClicksInActiveElement: false,
    }
  }

  return { clickLock, activateClickLock, activateBlockButtonsInActiveElement, deactivateClickLock }
}
